import { useEffect, useState } from 'react';

import { clearSession, writeSessionToken } from '@/lib/auth';
import { urlParams } from '@/app/urlParams';
import { ensureDevSessionToken } from '@/services/api/auth';
import { appConfig } from '@app/config/appConfig';

let sessionPromise: Promise<void> | null = null;

function ensureSession(stageId: string): Promise<void> {
  sessionPromise ??= ensureDevSessionToken(stageId)
    .then(() => undefined)
    .catch((error: unknown) => {
      sessionPromise = null;
      throw error;
    });

  return sessionPromise;
}

function resetSession(): void {
  sessionPromise = null;
  clearSession();
}

/** App-load auth: hydrate encrypted token or fetch dev-session when missing. */
export function useDevAuth() {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isReady) {
      return;
    }

    void (async () => {
      try {
        // Zitadel mode: token arrives via URL ?token=<jwt>
        if (urlParams.token) {
          await writeSessionToken(urlParams.token);
          setIsReady(true);
          return;
        }
        // No backend configured — gameplay runs fully offline, so skip the dev-session handshake.
        const hasBackend =
          appConfig.api.baseUrl.length > 0 || appConfig.api.mockUserSessionUrl.length > 0;
        if (!hasBackend) {
          setIsReady(true);
          return;
        }
        // Dev-session fallback
        const stageId = appConfig.stage.id;
        await ensureSession(stageId);
        setIsReady(true);
      } catch (error) {
        resetSession();
        setError(error instanceof Error ? error.message : 'Failed to start session');
      }
    })();
  }, [isReady]);

  return { isReady, error };
}
